import React from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import { CardConfidence } from 'containers/Intelligence/types';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

export interface IConfidenceReasonProps {
  /** Pre-ordered by materiality; only the first `MAX_REASONS` are material enough to publish. */
  reasons: CardConfidence['reasons'];
  className?: string;
}

// The FR caps the confidence line at three material reasons, so a supplier that sends more has the
// tail dropped here rather than in every calling card.
const MAX_REASONS = 3;

/* The caption step of the design system's type scale (Caption 11, 140% line), with a 700 Reason:
   and a 400 body as 3583-10327 draws them. PLA-8844. */
const REASON_SIZE = tokens.type['caption-size'];
const REASON_WEIGHT = '400';
const REASON_LINE = tokens.type['caption-line'];
const REASON_LABEL_WEIGHT = '700';

const ReasonLine = styled.p`
  margin: 0;
  color: ${tokens.color['text-primary']};
  font-size: ${REASON_SIZE};
  font-weight: ${REASON_WEIGHT};
  line-height: ${REASON_LINE};
`;

const ReasonLabel = styled.span`
  color: ${tokens.color['text-secondary']};
  font-weight: ${REASON_LABEL_WEIGHT};
`;

const ConfidenceReason: React.FC<IConfidenceReasonProps> = ({ reasons, className }) => {
  const { t } = useMultiLanguageTranslation();

  const materialReasons = Array.isArray(reasons) ? reasons.slice(0, MAX_REASONS) : [];

  if (materialReasons.length === 0) {
    return null;
  }

  const separator = t(`${TCustomerInsights}.Confidence.ReasonSeparator`);

  return (
    <ReasonLine className={className} data-testid="confidence-reason">
      {/* A real space, not a margin, so the copied and announced line reads `Reason: …`. */}
      <ReasonLabel data-testid="confidence-reason-label">
        {t(`${TCustomerInsights}.Confidence.ReasonLabel`)}
      </ReasonLabel>{' '}
      {materialReasons.map((reason, index) => (
        <React.Fragment key={reason}>
          {index > 0 ? separator : null}
          <span data-testid="confidence-reason-item">{reason}</span>
        </React.Fragment>
      ))}
    </ReasonLine>
  );
};

export default ConfidenceReason;
